import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import chatService from '../services/chatService';

import './Nav.css';

const Nav = ({ hideMessages = false, showBackButton = false }) => {
  const { user, logout, token } = useAuth();
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Cerrar al hacer clic fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll unread messages count
  useEffect(() => {
    if (!token || hideMessages) return;
    const fetchUnread = async () => {
      try {
        const data = await chatService.getUnreadCount(token);
        setUnreadMessages(data.count || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [token, hideMessages]);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    navigate('/');
  };

  return (
    <nav className="main-nav">
      <div className="nav-inner">
        <div className="nav-left-section">
          {showBackButton && (
            <button className="nav-back-btn" onClick={() => navigate(-1)}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Regresar
            </button>
          )}
          <div className="nav-logo">
            <Link to="/" className="branding-link">
              <img src="/logoecseia.png" alt="Logo" className="nav-logo-icon" />
              <span className="brand-eco">Eco</span>ScienceInnova
            </Link>
          </div>
        </div>

        <div className="nav-right-container">

          {/* Message notification icon */}
          {token && !hideMessages && (
            <button
              className="nav-msg-btn"
              onClick={() => navigate('/chat')}
              title="Mensajes"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {unreadMessages > 0 && (
                <span className="nav-msg-badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
              )}
            </button>
          )}

          <div className="nav-user-container">
            {!token ? (
              <Link to="/login" className="btn-login-nav">
                {t('login')}
              </Link>
            ) : (
              <div className="user-profile-dropdown" ref={dropdownRef}>
                <div
                  className="user-profile-trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div
                    className="user-avatar-circular"
                    onClick={(e) => {
                      e.stopPropagation();
                      const userRoles = user?.roles || [];
                      const isProfessionalOrInvestigator = userRoles.some(r => {
                        const roleName = typeof r === 'string' ? r : r.name;
                        return ['INVESTIGATOR', 'INSTITUTION', 'HEALTH_PROFESSIONAL'].includes(roleName);
                      });
                      navigate(isProfessionalOrInvestigator ? '/profile/edit' : '/profile-patient/edit');
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {user?.profile?.photoUrl ? (
                      <img
                        src={user.profile.photoUrl.startsWith('http')
                          ? user.profile.photoUrl
                          : `${process.env.REACT_APP_BACKEND_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '') : 'http://localhost:5000')}${user.profile.photoUrl.startsWith('/') ? '' : '/'}${user.profile.photoUrl}`}
                        alt="Avatar"
                      />
                    ) : (
                      <span className="avatar-initials">
                        {(user?.profile?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="nav-dropdown-arrow">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </div>
                </div>

                {isDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <div className="dropdown-user-info">
                      <span className="dropdown-name">
                        {user?.profile?.firstName} {user?.profile?.lastName}
                      </span>
                      <span className="dropdown-email">{user?.email}</span>
                      <span className="dropdown-role">
                        {user?.roles?.map(r => typeof r === 'string' ? r : r.name).join(', ')}
                      </span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div
                      className="dropdown-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const userRoles = user?.roles || [];
                        const isProfessionalOrInvestigator = userRoles.some(r => {
                          const roleName = typeof r === 'string' ? r : r.name;
                          return ['INVESTIGATOR', 'INSTITUTION', 'HEALTH_PROFESSIONAL'].includes(roleName);
                        });
                        setIsDropdownOpen(false);
                        navigate(isProfessionalOrInvestigator ? '/profile/edit' : '/profile-patient/edit');
                      }}
                    >
                      <svg className="item-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      {t('portal.userMenu.editProfile')}
                    </div>
                    <div className="dropdown-divider"></div>
                    <button
                      onClick={handleLogout}
                      className="dropdown-item logout-item"
                    >
                      <svg className="item-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      {t('portal.userMenu.logout')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav;
